import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { CourseStepSlotPicker } from '@/components/patient/CourseStepSlotPicker';
import { PortalIcon } from '@/components/portal/PortalIcon';
import { coursePortalStartPhrase, formatPortalDateTime } from '@/lib/portalFormat';
import {
  usePatientBookTreatmentCourseStep,
  usePatientCancelTreatmentCourseStep,
  usePortalTreatmentCourse,
} from '@/hooks/usePortalTreatmentCourses';
import type { TreatmentCourseStepSlot } from '@/hooks/useBookTreatmentCourse';
import {
  canPatientBookStep,
  canPatientCancelCourseStep,
  canPatientRebookStep,
  portalStepDisplayName,
  previousBookedStartsAt,
  type PortalTreatmentCourse,
  type PortalTreatmentCourseStep,
} from '@/lib/portalTreatmentCourse';
import { portalCourseForloebProgress } from '@/lib/courseStepSlotPicker';

function stepStatusKey(status: PortalTreatmentCourseStep['status']): string {
  switch (status) {
    case 'unscheduled':
      return 'portal.treatment_courses.step_unbooked';
    case 'upcoming':
      return 'portal.treatment_courses.step_booked';
    case 'completed':
      return 'portal.treatment_courses.step_completed';
    case 'cancelled':
      return 'portal.treatment_courses.step_cancelled';
    case 'no_show':
      return 'portal.treatment_courses.step_no_show';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function PortalTreatmentCourseDetail() {
  const { courseId } = useParams<{ courseId: string }>();
  const { t } = useTranslation();
  const { data: course, isLoading, error } = usePortalTreatmentCourse(courseId);
  const [pickerStepId, setPickerStepId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <section className="card">
        <p className="help">Henter forløb…</p>
      </section>
    );
  }

  if (error || !course) {
    return (
      <>
        <Link
          to="/portal/treatment-courses"
          className="link"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 14,
            marginBottom: 12,
          }}
        >
          <PortalIcon name="chevronLeft" size="sm" />
          Behandlingsforløb
        </Link>
        <section className="card">
          <p className="help">{t('portal.treatment_courses.load_error')}</p>
        </section>
      </>
    );
  }

  const progress = portalCourseForloebProgress(course.steps);
  const nextUpcoming = course.steps.find((step) => step.status === 'upcoming');
  const host =
    nextUpcoming?.practitioner_names.map((person) => person.name).join(', ') ||
    course.steps
      .find((step) => step.practitioner_names.length > 0)
      ?.practitioner_names.map((person) => person.name)
      .join(', ') ||
    '';
  const started = course.steps.find((step) => step.starts_at)?.starts_at;

  return (
    <>
      <Link
        to="/portal/treatment-courses"
        className="link"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 14,
          marginBottom: 12,
        }}
      >
        <PortalIcon name="chevronLeft" size="sm" />
        Behandlingsforløb
      </Link>
      <div className="pt-head">
        <div>
          <h1>{course.template_name}</h1>
          <p>
            {host ? `Hos ${host}` : t(`portal.treatment_courses.status_${course.status}`)}
            {coursePortalStartPhrase(started) ?? ''}
          </p>
        </div>
      </div>
      <section className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div className="segbar" style={{ flex: 1 }}>
            {course.steps.map((step, index) => (
              <i
                key={step.id}
                className={
                  step.status === 'completed' ? 'd' : index === progress.n - 1 ? 'n' : undefined
                }
              />
            ))}
          </div>
          <b style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
            {progress.completed} af {progress.total} trin gennemført
          </b>
        </div>
        <div className="steps" style={{ marginTop: 12 }}>
          {course.steps.map((step, index) => (
            <CourseStepRow
              key={step.id}
              course={course}
              step={step}
              isNext={index === progress.n - 1}
              pickerOpen={pickerStepId === step.id}
              onTogglePicker={() =>
                setPickerStepId((current) => (current === step.id ? null : step.id))
              }
            />
          ))}
        </div>
      </section>
      <p className="help" style={{ marginTop: 16 }}>
        Trinene skal ligge i rækkefølge. Klinikken bestemmer, om du selv kan booke og flytte trin.
        Betalte tider ændres via klinikken.
      </p>
    </>
  );
}

function CourseStepRow({
  course,
  step,
  isNext,
  pickerOpen,
  onTogglePicker,
}: {
  course: PortalTreatmentCourse;
  step: PortalTreatmentCourseStep;
  isNext: boolean;
  pickerOpen: boolean;
  onTogglePicker: () => void;
}) {
  const { t } = useTranslation();
  const bookable = canPatientBookStep(course, course.steps, step);
  const rebookable = canPatientRebookStep(course, step);
  const cancellable = canPatientCancelCourseStep(course, step);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const cancelStep = usePatientCancelTreatmentCourseStep();
  const practitionerLabel = step.practitioner_names.map((person) => person.name).join(', ');
  const mark =
    step.status === 'completed' ? 'done' : isNext || step.status === 'upcoming' ? 'next' : 'plan';
  const chips = stepChips(step, isNext, t);

  return (
    <div className="st">
      <span className={`mk ${mark}`}>{mark === 'done' ? <PortalIcon name="check" /> : null}</span>
      <div>
        <div style={{ fontSize: 16, lineHeight: '24px', fontWeight: 600 }}>
          Trin {step.sort_order} ·{' '}
          {portalStepDisplayName(step, t('treatment_courses.step_no_service'))}
        </div>
        <div className="help" style={{ fontSize: 14 }}>
          {step.starts_at
            ? [formatPortalDateTime(step.starts_at), practitionerLabel].filter(Boolean).join(' · ')
            : t(stepStatusKey(step.status))}
        </div>
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {chips.map((chip) => (
            <span key={chip.label} className={`chip chip-${chip.tone}`}>
              <PortalIcon name={chip.icon} size="xs" />
              {chip.label}
            </span>
          ))}
        </div>
        {confirmCancel ? (
          <div className="notice amber" style={{ marginTop: 12 }}>
            <PortalIcon name="info" size="sm" />
            <span>
              {t('portal.treatment_courses.cancel_confirm')}
              <span style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={cancelStep.isPending}
                  onClick={async () => {
                    try {
                      await cancelStep.mutateAsync({ stepId: step.id });
                      toast.success(t('portal.treatment_courses.cancel_toast'));
                      setConfirmCancel(false);
                    } catch (err) {
                      console.error('cancel course step failed', err);
                      toast.error(t('portal.treatment_courses.cancel_error'));
                    }
                  }}
                >
                  {t('portal.treatment_courses.cancel_action')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setConfirmCancel(false)}
                >
                  {t('common.close')}
                </button>
              </span>
            </span>
          </div>
        ) : null}
        {pickerOpen && (bookable || rebookable) ? (
          <SlotPicker course={course} step={step} rebook={rebookable && !bookable} />
        ) : null}
        {!course.patient_can_book_remaining && step.status === 'unscheduled' ? (
          <p className="help" style={{ marginTop: 8 }}>
            {t('portal.treatment_courses.booking_disabled')}
          </p>
        ) : null}
      </div>
      <div>
        {rebookable ? (
          <button type="button" className="btn btn-secondary" onClick={onTogglePicker}>
            <PortalIcon name="calendarMove" size="sm" />
            Flyt
          </button>
        ) : bookable ? (
          <button type="button" className="btn btn-primary" onClick={onTogglePicker}>
            <PortalIcon name="calendarPlus" size="sm" />
            Book
          </button>
        ) : null}
        {cancellable && !confirmCancel ? (
          <button
            type="button"
            className="btn btn-secondary"
            style={{ marginTop: rebookable || bookable ? 8 : 0 }}
            onClick={() => setConfirmCancel(true)}
          >
            {t('portal.treatment_courses.cancel_action')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function stepChips(
  step: PortalTreatmentCourseStep,
  isNext: boolean,
  t: (key: string) => string,
): Array<{
  tone: 'green' | 'teal' | 'neutral' | 'amber';
  icon: 'check' | 'clock' | 'calendar' | 'info';
  label: string;
}> {
  if (step.status === 'completed') {
    return [{ tone: 'green', icon: 'check', label: 'Gennemført' }];
  }
  if (isNext && step.status === 'upcoming') {
    return [{ tone: 'teal', icon: 'clock', label: 'Næste' }];
  }
  if (step.status === 'upcoming') {
    return [{ tone: 'neutral', icon: 'calendar', label: 'Booket' }];
  }
  if (step.status === 'unscheduled') {
    return [{ tone: 'amber', icon: 'info', label: t('portal.treatment_courses.step_unbooked') }];
  }
  return [{ tone: 'neutral', icon: 'info', label: t(stepStatusKey(step.status)) }];
}

function SlotPicker({
  course,
  step,
  rebook,
}: {
  course: PortalTreatmentCourse;
  step: PortalTreatmentCourseStep;
  rebook: boolean;
}) {
  const { t } = useTranslation();
  const previousStartsAt = previousBookedStartsAt(course.steps, step);
  const [selectedSlot, setSelectedSlot] = useState<TreatmentCourseStepSlot | null>(null);
  const bookStep = usePatientBookTreatmentCourseStep();
  const practitionerOptions = [...step.eligible_practitioners, ...step.practitioner_names];

  return (
    <div style={{ marginTop: 12 }}>
      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
        {rebook
          ? t('portal.treatment_courses.rebook_title')
          : t('portal.treatment_courses.choose_time')}
      </h3>

      <CourseStepSlotPicker
        clinicId={course.clinic_id}
        courseId={course.id}
        bookedStepId={step.id}
        unbooked={!rebook && step.status === 'unscheduled'}
        bookedStartsAt={step.starts_at}
        previousStartsAt={previousStartsAt}
        intervalValue={step.interval_value}
        intervalUnit={step.interval_unit}
        toleranceValue={step.interval_tolerance_value}
        toleranceUnit={step.interval_tolerance_unit}
        practitionerOptions={practitionerOptions}
        patientId={course.patient_id}
        stickyPractitionerIds={[
          ...step.practitioner_ids,
          ...step.practitioner_names.map((person) => person.id),
        ]}
        selectedSlot={selectedSlot}
        enabled
        onSelectSlot={setSelectedSlot}
      />

      <button
        type="button"
        className="btn btn-primary"
        style={{ marginTop: 12 }}
        disabled={!selectedSlot || bookStep.isPending}
        onClick={async () => {
          if (!selectedSlot) return;
          try {
            await bookStep.mutateAsync({
              courseId: course.id,
              stepId: step.id,
              startsAt: selectedSlot.starts_at,
              practitionerIds: selectedSlot.practitioner_ids,
            });
            toast.success(t('portal.treatment_courses.book_toast'));
            setSelectedSlot(null);
          } catch (err) {
            console.error('patient book course step failed', err);
            toast.error(t('portal.treatment_courses.book_error'));
          }
        }}
      >
        {t('portal.treatment_courses.book_action')}
      </button>
    </div>
  );
}
